import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { AssetItem, RecommendedAsset } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../../ActionLibraryInstanceForm.types';

import AssetSearchField from './components/AssetSearchField';
import AssetSection from './components/AssetSection';
import LoadingState from './components/LoadingState';
import useAssetFiltering from './hooks/useAssetFiltering';
import useAssetSelection from './hooks/useAssetSelection';
import useAssetSelectionData from './hooks/useAssetSelectionData';
import useAutoSelectRecommended from './hooks/useAutoSelectRecommended';

const AssetSelectionList: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<ActionLibraryInstanceFormValues>();
  const [search, setSearch] = useState('');

  const { selectedTemplate } = values;
  const templateId = selectedTemplate?.id;

  // Fetch asset data
  const { recommendedAssets, availableAssets, isLoading } = useAssetSelectionData({
    templateId,
    search,
  });

  // Manage selection state
  const {
    selectedAssets,
    toggleRecommended,
    toggleAvailable,
    selectAll: handleSelectAll,
    deselectAll: handleDeselectAll,
    addAssets,
  } = useAssetSelection();

  // Auto-select recommended assets when they are first loaded
  useAutoSelectRecommended({
    recommendedAssets,
    isLoading,
    templateId,
    onAddAssets: addAssets,
  });

  // Filter assets based on search
  const { filteredRecommended, filteredOthers } = useAssetFiltering({
    recommendedAssets,
    availableAssets,
    search,
  });

  // Get selected asset IDs for easier comparison
  const selectedAssetIds = useMemo(() => selectedAssets.map(a => Number(a.id)), [selectedAssets]);

  // Type guard to check if asset is RecommendedAsset
  const isRecommendedAsset = useCallback(
    (asset: RecommendedAsset | AssetItem): asset is RecommendedAsset => 'type' in asset && 'importance' in asset,
    [],
  );

  // Wrapper handlers that accept union type
  const handleToggleRecommended = useCallback<(asset: RecommendedAsset | AssetItem) => void>(
    (asset: RecommendedAsset | AssetItem) => {
      if (isRecommendedAsset(asset)) {
        toggleRecommended(asset);
      }
    },
    [isRecommendedAsset, toggleRecommended],
  );

  const handleToggleAvailable = useCallback<(asset: RecommendedAsset | AssetItem) => void>(
    (asset: RecommendedAsset | AssetItem) => {
      if (!isRecommendedAsset(asset)) {
        toggleAvailable(asset);
      }
    },
    [isRecommendedAsset, toggleAvailable],
  );

  // Handlers for section-specific actions
  const handleSelectAllRecommended = () => {
    handleSelectAll(filteredRecommended);
  };

  const handleDeselectAllRecommended = () => {
    handleDeselectAll(filteredRecommended);
  };

  const handleSelectAllAvailable = () => {
    handleSelectAll(filteredOthers);
  };

  const handleDeselectAllAvailable = () => {
    handleDeselectAll(filteredOthers);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box>
      <AssetSearchField onSearchChange={setSearch} search={search} />

      {templateId && (
        <AssetSection
          assets={filteredRecommended}
          emptyMessage={
            action_library_messages.instance.asset_selection.no_recommended_assets ||
            action_library_messages.instance.asset_selection.no_assets_found
          }
          isRecommended
          onDeselectAll={handleDeselectAllRecommended}
          onSelectAll={handleSelectAllRecommended}
          onToggle={handleToggleRecommended}
          selectedAssetIds={selectedAssetIds}
          title={t(action_library_messages.instance.asset_selection.recommended_section)}
        />
      )}

      <AssetSection
        assets={filteredOthers}
        emptyMessage={action_library_messages.instance.asset_selection.no_assets_found}
        onDeselectAll={handleDeselectAllAvailable}
        onSelectAll={handleSelectAllAvailable}
        onToggle={handleToggleAvailable}
        selectedAssetIds={selectedAssetIds}
        title={t(action_library_messages.instance.asset_selection.others_section)}
      />
    </Box>
  );
};

export default AssetSelectionList;
